import { useState } from 'react';
import { 
  Globe, Calendar, Mail, Users, BarChart3, MessageSquare, Image, FileText, 
  Settings, Shield, Clock, MapPin, Phone, Bell, Zap, CheckCircle, 
  Smartphone, Search, TrendingUp, Database, Link2, Upload, Download,
  Star, Sparkles, Target, Layers
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

const FEATURE_CATEGORIES = [
  {
    id: 'website',
    title: 'Professional Website',
    icon: Globe,
    color: 'bg-blue-500',
    features: [
      { name: 'Modern Responsive Design', description: 'Beautiful on desktop, tablet & mobile', status: 'live' },
      { name: 'SEO Optimized', description: 'Built for search engine visibility', status: 'live' },
      { name: 'Fast Loading', description: 'Optimized performance for quick page loads', status: 'live' },
      { name: 'Professional Branding', description: 'Custom logo and color scheme', status: 'live' },
      { name: 'Service Pages', description: 'Detailed information about all services', status: 'live' },
      { name: 'Before & After Gallery', description: 'Showcase transformations with image comparisons', status: 'live' },
      { name: 'Blog System', description: 'Share news, tips, and educational content', status: 'live' },
      { name: 'FAQ Section', description: 'Answer common patient questions', status: 'live' },
      { name: 'Cost Comparison Page', description: 'Transparent pricing information', status: 'live' },
      { name: 'Insurance & Payment Info', description: 'Payment options and insurance details', status: 'live' },
    ]
  },
  {
    id: 'appointments',
    title: 'Smart Appointment System',
    icon: Calendar,
    color: 'bg-orange-500',
    features: [
      { name: 'Multi-Step Booking Flow', description: '4-step guided appointment request', status: 'live' },
      { name: 'Preferred Time Selection', description: 'Clients choose multiple available times', status: 'live' },
      { name: 'Admin Offer System', description: 'Review requests and offer specific times', status: 'live' },
      { name: 'Client Confirmation', description: 'Secure email links for appointment confirmation', status: 'live' },
      { name: 'Automated Reminders', description: '24-hour and 2-hour email reminders', status: 'live' },
      { name: 'Google Calendar Sync', description: 'Confirmed appointments auto-add to your calendar', status: 'live' },
      { name: 'Per-Day Business Hours', description: 'Set different hours for each day of the week', status: 'live' },
      { name: 'Calendar View', description: 'Visual calendar of all appointments', status: 'live' },
      { name: 'Status Tracking', description: 'Track pending, offered, confirmed appointments', status: 'live' },
    ]
  },
  {
    id: 'leads',
    title: 'Lead Management',
    icon: Users,
    color: 'bg-green-500',
    features: [
      { name: 'Contact Form Capture', description: 'All inquiries saved automatically', status: 'live' },
      { name: 'Lead Scoring', description: 'Automatic scoring based on engagement', status: 'live' },
      { name: 'City Tracking', description: 'Know where your leads come from', status: 'live' },
      { name: 'Service Interest Tracking', description: 'Track which services attract leads', status: 'live' },
      { name: 'CSV Import', description: 'Upload existing leads from spreadsheets', status: 'live' },
      { name: 'CSV Export', description: 'Download leads for external use', status: 'live' },
      { name: 'Search & Filter', description: 'Quickly find leads by name, email, city', status: 'live' },
    ]
  },
  {
    id: 'email',
    title: 'Email Marketing',
    icon: Mail,
    color: 'bg-purple-500',
    features: [
      { name: 'Welcome Email Sequence', description: 'Automated 3-email series for new contacts', status: 'live' },
      { name: 'Custom Email Sequences', description: 'Create multi-step email campaigns', status: 'live' },
      { name: 'Broadcast Emails', description: 'Send announcements to all contacts', status: 'live' },
      { name: 'Marketing Contact Import', description: 'Upload contact lists via CSV', status: 'live' },
      { name: 'Email Statistics', description: 'Track sent, delivered, and engagement', status: 'live' },
      { name: 'Professional Templates', description: 'Branded email designs with your logo', status: 'live' },
      { name: 'Appointment Notifications', description: 'Automated emails for booking workflow', status: 'live' },
    ]
  },
  {
    id: 'analytics',
    title: 'Visitor Analytics',
    icon: BarChart3,
    color: 'bg-cyan-500',
    features: [
      { name: 'Visit Tracking', description: 'Track every visitor to your website', status: 'live' },
      { name: 'Geographic Data', description: 'See which cities visitors come from', status: 'live' },
      { name: 'Page View Analytics', description: 'Know which pages are most popular', status: 'live' },
      { name: 'Dashboard Summary', description: 'Quick overview of key metrics', status: 'live' },
      { name: 'Conversion Tracking', description: 'Track leads and appointments', status: 'live' },
    ]
  },
  {
    id: 'chat',
    title: 'AI Chat Assistant',
    icon: MessageSquare,
    color: 'bg-pink-500',
    features: [
      { name: 'Instant Responses', description: '24/7 automated answers to common questions', status: 'live' },
      { name: 'Service Information', description: 'Explains services, pricing, and process', status: 'live' },
      { name: 'Appointment Scheduling', description: 'Guides visitors to book consultations', status: 'live' },
      { name: 'Location & Hours', description: 'Provides contact info instantly', status: 'live' },
      { name: 'Insurance Questions', description: 'Answers payment and coverage questions', status: 'live' },
    ]
  },
  {
    id: 'content',
    title: 'Content Management',
    icon: FileText,
    color: 'bg-amber-500',
    features: [
      { name: 'Blog Management', description: 'Create, edit, publish blog posts', status: 'live' },
      { name: 'Gallery Management', description: 'Upload and organize before/after images', status: 'live' },
      { name: 'FAQ Management', description: 'Add and organize frequently asked questions', status: 'live' },
      { name: 'Service Management', description: 'Update service descriptions and pricing', status: 'live' },
      { name: 'Direct Image Upload', description: 'Upload images directly in admin panel', status: 'live' },
      { name: 'SEO Fields', description: 'Meta descriptions for search optimization', status: 'live' },
    ]
  },
  {
    id: 'contact',
    title: 'Contact & Location',
    icon: MapPin,
    color: 'bg-red-500',
    features: [
      { name: 'Interactive Google Map', description: 'Embedded map showing your location', status: 'live' },
      { name: 'Click-to-Call', description: 'Tap phone number to call instantly', status: 'live' },
      { name: 'Get Directions', description: 'One-click Google Maps directions', status: 'live' },
      { name: 'Business Hours Display', description: 'Shows your hours on every page', status: 'live' },
      { name: 'Contact Form', description: 'Easy way for visitors to reach you', status: 'live' },
    ]
  },
  {
    id: 'admin',
    title: 'Secure Admin Panel',
    icon: Shield,
    color: 'bg-slate-600',
    features: [
      { name: 'Secure Login', description: 'Password-protected admin access', status: 'live' },
      { name: 'Dashboard Overview', description: 'Quick stats and recent activity', status: 'live' },
      { name: 'Mobile-Friendly Admin', description: 'Manage from any device', status: 'live' },
      { name: 'Settings Configuration', description: 'Customize system behavior', status: 'live' },
      { name: 'Feature Documentation', description: 'This page - always up to date!', status: 'live' },
    ]
  },
];

const UPCOMING_FEATURES = [
  { name: 'SMS Reminders', description: 'Text message appointment reminders', icon: Phone },
  { name: 'City Landing Pages', description: '7 dedicated pages for Oregon Coast cities', icon: MapPin },
  { name: 'Heatmap Tracking', description: 'See where visitors click on your site', icon: Target },
  { name: 'Advanced AI Chat', description: 'Pain and sleep apnea escalation handling', icon: Sparkles },
  { name: 'CRM Integration', description: 'Connect with external CRM systems', icon: Link2 },
];

const STATS = [
  { label: 'Total Features', value: '60+', icon: Layers },
  { label: 'Pages', value: '10+', icon: FileText },
  { label: 'Integrations', value: '4', icon: Link2 },
  { label: 'Automations', value: '5+', icon: Zap },
];

export default function AdminFeatures() {
  const [selectedCategory, setSelectedCategory] = useState(null);

  const totalFeatures = FEATURE_CATEGORIES.reduce((acc, cat) => acc + cat.features.length, 0);
  const liveFeatures = FEATURE_CATEGORIES.reduce(
    (acc, cat) => acc + cat.features.filter(f => f.status === 'live').length, 0
  );

  return (
    <div data-testid="admin-features" className="pb-12">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Feature Showcase
            </h1>
            <p className="text-slate-500">Everything included in your website package</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {STATS.map((stat, idx) => (
          <Card key={idx} className="border-none shadow-sm bg-gradient-to-br from-slate-50 to-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <stat.icon className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-xs text-slate-500">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Live Features Count */}
      <Card className="border-none shadow-sm mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-l-green-500">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <p className="font-semibold text-green-800">All Features Live & Working</p>
              <p className="text-sm text-green-600">{liveFeatures} of {totalFeatures} features fully operational</p>
            </div>
          </div>
          <Badge className="bg-green-600 hover:bg-green-700">100% Complete</Badge>
        </CardContent>
      </Card>

      {/* Feature Categories */}
      <div className="grid gap-6">
        {FEATURE_CATEGORIES.map((category) => (
          <Card key={category.id} className="border-none shadow-sm overflow-hidden">
            <CardHeader 
              className={`${category.color} text-white cursor-pointer transition-all hover:opacity-90`}
              onClick={() => setSelectedCategory(selectedCategory === category.id ? null : category.id)}
            >
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <category.icon className="w-6 h-6" />
                  <span>{category.title}</span>
                </div>
                <Badge variant="secondary" className="bg-white/20 text-white hover:bg-white/30">
                  {category.features.length} features
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className={`p-0 transition-all duration-300 ${selectedCategory === category.id || selectedCategory === null ? 'max-h-[2000px]' : 'max-h-0 overflow-hidden'}`}>
              <div className="p-4 grid gap-2">
                {category.features.map((feature, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{feature.name}</p>
                      <p className="text-sm text-slate-500">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Upcoming Features */}
      <Card className="border-none shadow-sm mt-8">
        <CardHeader className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
          <CardTitle className="flex items-center gap-3">
            <TrendingUp className="w-6 h-6" />
            Coming Soon
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {UPCOMING_FEATURES.map((feature, idx) => (
              <div 
                key={idx} 
                className="flex items-start gap-3 p-4 rounded-xl border border-dashed border-slate-300 bg-slate-50"
              >
                <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center">
                  <feature.icon className="w-5 h-5 text-slate-500" />
                </div>
                <div>
                  <p className="font-medium text-slate-700">{feature.name}</p>
                  <p className="text-sm text-slate-500">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Footer Note */}
      <div className="mt-8 text-center text-sm text-slate-500">
        <p>Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
        <p className="mt-1">This page automatically reflects the current state of your website features.</p>
      </div>
    </div>
  );
}
